import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isMoonbaseConfigured, siteConfig } from '../config/site';

type MoonbaseStatus = 'disabled' | 'loading' | 'ready' | 'error';

type MoonbaseContextValue = {
  status: MoonbaseStatus;
  configured: boolean;
  error: string | null;
  openSetup: () => void;
  setupOpen: boolean;
  closeSetup: () => void;
  signIn: () => void;
  signUp: () => void;
  viewAccount: () => void;
  viewProducts: () => void;
  viewSubscriptions: () => void;
  viewCart: () => void;
  addProduct: (productId: string) => Promise<void>;
  buyProduct: (productId: string) => Promise<void>;
  viewProduct: (productId: string) => void;
  downloadProduct: (productId: string) => void;
  activateProduct: () => void;
  redeemVoucher: () => void;
  subscribe: (email?: string) => void;
};

const MoonbaseContext = createContext<MoonbaseContextValue | null>(null);
let setupPromise: Promise<void> | null = null;

function loadMoonbaseScript(): Promise<void> {
  if (window.Moonbase) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>('script[data-wavecraft-moonbase]');
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Moonbase storefront failed to load.')), {
        once: true,
      });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = siteConfig.moonbase.storefrontScript;
    script.dataset.wavecraftMoonbase = 'true';
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Moonbase storefront failed to load.')), {
      once: true,
    });
    document.head.appendChild(script);
  });
}

async function initializeMoonbase(): Promise<void> {
  if (!isMoonbaseConfigured()) return;
  if (setupPromise) return setupPromise;

  setupPromise = (async () => {
    await loadMoonbaseScript();
    if (!window.Moonbase) throw new Error('Moonbase loaded without exposing its storefront API.');

    await window.Moonbase.setup(siteConfig.moonbase.accountUrl, {
      toolbar: { enabled: false },
      auth: {
        signIn: { enabled: true, hint: 'Use the email address connected to your purchase.' },
        signUp: { enabled: true, marketingConsent: 'OptIn' },
        passwords: 'default',
      },
      checkout: { redirect: false },
      cart: {
        quantity: 'single',
        bundles: { onAdd: 'replace', showProducts: true },
      },
      activation: {
        deviceTokenFileExtension: '.dt',
        licenseTokenFileName: 'wavecraft-license.mb',
        websiteLink: { enabled: true, label: `Back to ${siteConfig.brand.shortName}` },
      },
      pricing: { trailingZeros: 'auto', currencyDisplay: 'narrowSymbol' },
      theme: {
        dark: true,
        colors: { primary: '#f5a20b', background: 'gray' },
        fonts: { heading: 'Montserrat', body: 'Inter' },
        corners: 'soft',
        buttons: 'light',
        cards: 'shadow',
      },
    });
  })().catch((reason: unknown) => {
    setupPromise = null;
    throw reason;
  });

  return setupPromise;
}

export function MoonbaseProvider({ children }: PropsWithChildren) {
  const configured = isMoonbaseConfigured();
  const [status, setStatus] = useState<MoonbaseStatus>(configured ? 'loading' : 'disabled');
  const [error, setError] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);

  useEffect(() => {
    if (!configured) return;
    let active = true;

    initializeMoonbase()
      .then(() => {
        if (active) setStatus('ready');
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setStatus('error');
        setError(reason instanceof Error ? reason.message : 'Moonbase could not be initialized.');
      });

    return () => {
      active = false;
    };
  }, [configured]);

  const requireMoonbase = useCallback((): NonNullable<Window['Moonbase']> | null => {
    if (!configured || !window.Moonbase) {
      setSetupOpen(true);
      return null;
    }
    return window.Moonbase;
  }, [configured]);

  const value = useMemo<MoonbaseContextValue>(
    () => ({
      status,
      configured,
      error,
      setupOpen,
      openSetup: () => setSetupOpen(true),
      closeSetup: () => setSetupOpen(false),
      signIn: () => requireMoonbase()?.sign_in(),
      signUp: () => requireMoonbase()?.sign_up(),
      viewAccount: () => requireMoonbase()?.view_account(),
      viewProducts: () => requireMoonbase()?.view_products(),
      viewSubscriptions: () => requireMoonbase()?.view_subscriptions(),
      viewCart: () => requireMoonbase()?.view_cart(),
      addProduct: async (productId: string) => {
        await requireMoonbase()?.add_to_cart({ product_id: productId, show_cart: true });
      },
      buyProduct: async (productId: string) => {
        await requireMoonbase()?.purchase({ product_id: productId });
      },
      viewProduct: (productId: string) => requireMoonbase()?.view_product({ product_id: productId }),
      downloadProduct: (productId: string) =>
        requireMoonbase()?.download_product({ product_id: productId }),
      activateProduct: () => requireMoonbase()?.activate_product(),
      redeemVoucher: () => requireMoonbase()?.redeem_voucher(),
      subscribe: (email?: string) => requireMoonbase()?.subscribe(email ? { email } : undefined),
    }),
    [configured, error, requireMoonbase, setupOpen, status],
  );

  return <MoonbaseContext.Provider value={value}>{children}</MoonbaseContext.Provider>;
}

export function useMoonbase(): MoonbaseContextValue {
  const value = useContext(MoonbaseContext);
  if (!value) throw new Error('useMoonbase must be used inside MoonbaseProvider.');
  return value;
}

export function enableMoonbaseGoogleAnalytics(measurementId: string): void {
  if (!measurementId || !window.Moonbase) return;
  window.Moonbase.configure({ integrations: { googleAnalytics: { measurementId } } });
}
